import { CameraOff } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import type { ImageMeta } from "@/assets/imageIndex";
import { cn } from "@/lib/utils";

type ImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src: string | ImageMeta;
  loaderClassName?: string;
  alt: string;
  priority?: boolean;
};

export function Image({
  loaderClassName,
  className,
  src: srcProp,
  alt,
  priority,
  ...rest
}: ImageProps) {
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const isMetaImg = srcProp != null && typeof srcProp === "object";
  const src = srcProp ? (isMetaImg ? srcProp.src : srcProp) : undefined;
  // Emit real intrinsic dimensions when known (meta import or explicit props) so
  // the browser reserves space and avoids CLS; omit the attrs otherwise rather
  // than emitting an invalid empty string.
  const intrinsicWidth = isMetaImg ? srcProp.width : rest.width;
  const intrinsicHeight = isMetaImg ? srcProp.height : rest.height;

  const [prevSrc, setPrevSrc] = useState(src);
  if (src !== prevSrc) {
    setPrevSrc(src);
    setLoaded(false);
    setErrored(false);
  }

  useEffect(() => {
    const img = ref.current;
    if (img && img.complete && img.naturalWidth > 0) setLoaded(true);
  }, [src]);

  if (errored) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg bg-gray-200 ${className ?? ""}`}
      >
        <CameraOff className="w-10 h-10 text-gray-400" />
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-lg img-container`}>
      <img
        ref={ref}
        src={src}
        alt={alt}
        width={intrinsicWidth}
        height={intrinsicHeight}
        decoding={priority ? "auto" : "async"}
        loading={priority ? "eager" : (rest.loading ?? "lazy")}
        fetchPriority={priority ? "high" : undefined}
        {...rest}
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
        className={cn(
          "block w-full h-auto transition-opacity duration-1000",
          loaded ? "opacity-100" : "opacity-0",
          className,
        )}
      />

      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 rounded bg-gray-200 transition-opacity duration-1000 ${
          loaded ? "hidden" : "opacity-100 animate-pulse"
        } ${loaderClassName ?? ""}`}
      />
    </div>
  );
}
